import type { StateCreator } from "../store";
import type { AppStore } from "../index";

export type RecoveryStep = "idle" | "generating" | "verifying" | "complete" | "error";
export type SetupStep = "idle" | "generating" | "displaying" | "verifying" | "saving" | "complete";

export interface VerificationWord {
  word: string;
  index: number;
}

export interface AuthKeyPair {
  publicKey: string;
  privateKey: string;
}

export interface RecoverySlice {
  // Mnemonic and keypair state
  mnemonic: string | null;
  authKeyPair: AuthKeyPair | null;

  // Recovery process state
  isSetupComplete: boolean;
  isRecovering: boolean;
  recoveryStep: RecoveryStep;

  // Error handling
  recoveryError: string | null;

  // Account recovery data
  encryptedData: string | null;
  isDataRestored: boolean;

  // Setup flow state
  setupStep: SetupStep;
  verificationWords: VerificationWord[] | null;

  // Server registration state
  isAuthKeyRegistered: boolean;
  registrationError: string | null;

  // Post-restore peer-recovery banner state
  recoveryAnnounceActive: boolean;
  recoveryReshareCount: number;

  // Actions
  setMnemonic: (mnemonic: string | null) => void;
  setAuthKeyPair: (keyPair: AuthKeyPair | null) => void;
  setIsSetupComplete: (isComplete: boolean) => void;
  setIsRecovering: (isRecovering: boolean) => void;
  setRecoveryStep: (step: RecoveryStep) => void;
  setRecoveryError: (error: string | null) => void;
  setEncryptedData: (data: string | null) => void;
  setIsDataRestored: (isRestored: boolean) => void;
  setSetupStep: (step: SetupStep) => void;
  setVerificationWords: (words: VerificationWord[] | null) => void;
  setIsAuthKeyRegistered: (isRegistered: boolean) => void;
  setRegistrationError: (error: string | null) => void;
  startRecoveryAnnounceWindow: () => void;
  recordRecoveryReshare: () => void;
  dismissRecoveryAnnounceWindow: () => void;
  resetRecoveryState: () => void;
}

const initialState = {
  mnemonic: null,
  authKeyPair: null,
  isSetupComplete: false,
  isRecovering: false,
  recoveryStep: "idle" as RecoveryStep,
  recoveryError: null,
  encryptedData: null,
  isDataRestored: false,
  setupStep: "idle" as SetupStep,
  verificationWords: null,
  isAuthKeyRegistered: false,
  registrationError: null,
  recoveryAnnounceActive: false,
  recoveryReshareCount: 0,
};

export const createRecoverySlice: StateCreator<AppStore, unknown, unknown, RecoverySlice> = (set) => ({
  // Initial state
  ...initialState,

  // Actions
  setMnemonic: (mnemonic) =>
    set((state) => {
      state.mnemonic = mnemonic;
    }),

  setAuthKeyPair: (keyPair) =>
    set((state) => {
      state.authKeyPair = keyPair;
    }),

  setIsSetupComplete: (isComplete) =>
    set((state) => {
      state.isSetupComplete = isComplete;
    }),

  setIsRecovering: (isRecovering) =>
    set((state) => {
      state.isRecovering = isRecovering;
    }),

  setRecoveryStep: (step) =>
    set((state) => {
      state.recoveryStep = step;
    }),

  setRecoveryError: (error) =>
    set((state) => {
      state.recoveryError = error;
    }),

  setEncryptedData: (data) =>
    set((state) => {
      state.encryptedData = data;
    }),

  setIsDataRestored: (isRestored) =>
    set((state) => {
      state.isDataRestored = isRestored;
    }),

  setSetupStep: (step) =>
    set((state) => {
      state.setupStep = step;
    }),

  setVerificationWords: (words) =>
    set((state) => {
      state.verificationWords = words;
    }),

  setIsAuthKeyRegistered: (isRegistered) =>
    set((state) => {
      state.isAuthKeyRegistered = isRegistered;
    }),

  setRegistrationError: (error) =>
    set((state) => {
      state.registrationError = error;
    }),

  startRecoveryAnnounceWindow: () =>
    set((state) => {
      state.recoveryAnnounceActive = true;
      state.recoveryReshareCount = 0;
    }),

  recordRecoveryReshare: () =>
    set((state) => {
      if (state.recoveryAnnounceActive) {
        state.recoveryReshareCount = (state.recoveryReshareCount || 0) + 1;
      }
    }),

  dismissRecoveryAnnounceWindow: () =>
    set((state) => {
      state.recoveryAnnounceActive = false;
    }),

  resetRecoveryState: () =>
    set((state) => {
      Object.assign(state, initialState);
    }),
});
